/** Shown when HTML/script-like content is detected in a plain-text field. */
export const PLAIN_TEXT_MARKUP_MESSAGE = "HTML and script tags are not allowed in this field";

const MARKUP_PATTERNS = [
  /<\s*\/?\s*[a-z][\w-]*[\s>/]/i,
  /<\s*!\s*--/,
  /<\s*!\s*doctype/i,
  /<\s*\?/,
  /javascript\s*:/i,
  /vbscript\s*:/i,
  /data\s*:\s*text\/html/i,
  /\bon\w+\s*=/i,
  /<\/\s*script/i,
  /<\/\s*iframe/i,
  /<\/\s*object/i,
  /<\/\s*embed/i,
  /<\/\s*svg/i,
];

/**
 * @param {string} value
 * @returns {boolean}
 */
export function containsDisallowedMarkup(value) {
  if (value == null) return false;
  const text = String(value);
  if (text === "") return false;
  return MARKUP_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Strip control characters and angle-bracket segments from user input.
 * @param {string} value
 * @param {{ maxLength?: number }} [options]
 * @returns {string}
 */
export function sanitizePlainTextInput(value, options = {}) {
  const { maxLength } = options;
  let text = String(value ?? "");
  text = text.replace(/\0/g, "");
  text = text.replace(/<[^>]*>/g, "");
  if (maxLength != null && Number.isFinite(maxLength)) {
    text = text.slice(0, maxLength);
  }
  return text;
}

/**
 * @param {string} value
 * @param {{ fieldLabel?: string, required?: boolean, maxLength?: number }} [options]
 * @returns {string|null}
 */
export function validatePlainTextField(value, options = {}) {
  const { fieldLabel = "Field", required = false, maxLength } = options;
  const trimmed = String(value ?? "").trim();

  if (required && trimmed === "") {
    return `${fieldLabel} is required`;
  }
  if (trimmed === "") return null;

  if (maxLength != null && trimmed.length > maxLength) {
    return `${fieldLabel} must be at most ${maxLength} characters`;
  }
  if (containsDisallowedMarkup(trimmed)) {
    return PLAIN_TEXT_MARKUP_MESSAGE;
  }
  return null;
}

/**
 * @param {Record<string, string>} fields
 * @param {{ requiredFields?: string[] }} [options]
 * @returns {string|null}
 */
export function getPlainTextFieldsValidationError(fields, options = {}) {
  const required = new Set(options.requiredFields ?? []);
  for (const [label, raw] of Object.entries(fields)) {
    const error = validatePlainTextField(raw, {
      fieldLabel: label,
      required: required.has(label),
    });
    if (error) return error;
  }
  return null;
}
